// system modules
import { APP_INITIALIZER, ErrorHandler, NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { HttpClientModule, HTTP_INTERCEPTORS, HttpClient } from "@angular/common/http";

// app modules
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { CoreModule } from "./core/core.module";
import { GlobalErrorHandler } from "./core";
import { environment } from "src/environments/environment";
import { BROWSER_STORAGE, APP_CONFIG_TOKEN } from "./injection-tokens";
import { applicationConfigs, loginRequest, msalConfig } from "./app.config";
import devextremeAjax from "devextreme/core/utils/ajax";

// vendors modules
import {
  DxLoadIndicatorModule,
  DxPopupModule,
  DxLoadPanelModule,
  DxFormModule,
  DxSelectBoxModule,
  DxButtonModule,
  DxAccordionModule,
  DxToolbarModule,
  DxDataGridModule,
  DxTextBoxModule,
  DxValidatorModule,
  DxCheckBoxModule,
  DxFileUploaderModule,
  DxListModule,
  DxTagBoxModule,
  DxTextAreaModule,
  DxTooltipModule,
  DxProgressBarModule,
  DxCircularGaugeModule,
  DxNumberBoxModule,
  DxDateBoxModule,
  DxHtmlEditorModule
} from "devextreme-angular";
import { CacheLookupPolicy, IPublicClientApplication, InteractionType, PublicClientApplication } from "@azure/msal-browser";
import {
  MsalModule,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService
} from "@azure/msal-angular";
import { CommonModule } from "@angular/common";
import { SharedModule } from "./shared/shared.module";
import { ActionComponent } from "./action/action.component";
import { sendRequestFactory } from "src/app/core";
import { ReactiveFormsModule } from "@angular/forms";
import { ModuleEditorComponent, DemoAssetEditorComponent, SuggestedEditorComponent } from "./demo-asset-editor";
import { FileUploaderModule } from "@board/cloud-library/components/file-uploader";
import { CarouselModule } from "@board/cloud-library/components/carousel";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { MatTabsModule } from "@angular/material/tabs";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { DemoTemplateEditorComponent } from "./demo-template-editor/demo-template-editor.component";
import { MatIconModule } from "@angular/material/icon";
import { MatRadioModule } from "@angular/material/radio";
import { ApplicationInsightsService } from "@board/cloud-library/components/services";

export function initializeAppInsights(appInsights: ApplicationInsightsService): () => void {
  return () => appInsights.init(applicationConfigs.appInsightsKey, applicationConfigs.appInsightsConnectionString);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  // const protectedResourceMap = new Map();
  const protectedResourceMap = new Map([
    ["api/DemoInstance", [environment.apiScope]],
    ["api/DemoInstance/*", [environment.apiScope]],
    ["api/AzureRegion/*", [environment.apiScope]],
    ["api/DemoAsset", [environment.apiScope]],
    ["api/DemoAsset/*", [environment.apiScope]],
    ["api/Metadata/*", [environment.apiScope]],
    ["api/Template/*", [environment.apiScope]],
    ["api/DemoNews/*", [environment.apiScope]],
    ["api/User/*", [environment.apiScope]],
    ["api/Support", [environment.apiScope]],
    ["api/Support/*", [environment.apiScope]],
    ["api/Maintenance", [environment.apiScope]],
    ["api/Maintenance/*", [environment.apiScope]],
    ["api/Faq/*", [environment.apiScope]]
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
    // Prevent hidden-iframe token renewal (which can fail with X-Frame-Options and cause redirect loops).
    // If the access token isn't in cache, the interceptor will fall back to the configured interactionType.
    authRequest: {
      cacheLookupPolicy: CacheLookupPolicy.AccessToken
    }
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: loginRequest,
    // This route is already unguarded in app-routing.module.ts.
    // Sending users here on auth failures avoids infinite refresh/redirect loops.
    loginFailedRoute: "/session-expired"
  };
}

@NgModule({
  declarations: [
    AppComponent,
    ActionComponent,
    DemoAssetEditorComponent,
    ModuleEditorComponent,
    DemoTemplateEditorComponent,
    SuggestedEditorComponent
  ],
  imports: [
    CommonModule,
    SharedModule,
    BrowserModule,
    HttpClientModule,
    AppRoutingModule,
    CoreModule,
    DxLoadIndicatorModule,
    DxPopupModule,
    DxLoadPanelModule,
    DxFormModule,
    DxSelectBoxModule,
    DxButtonModule,
    DxAccordionModule,
    DxToolbarModule,
    DxDataGridModule,
    DxTextBoxModule,
    DxValidatorModule,
    DxCheckBoxModule,
    DxFileUploaderModule,
    DxListModule,
    DxTagBoxModule,
    DxTextAreaModule,
    DxTooltipModule,
    DxProgressBarModule,
    DxCircularGaugeModule,
    MsalModule,
    ReactiveFormsModule,
    FileUploaderModule,
    CarouselModule,
    BrowserAnimationsModule,
    MatTabsModule,
    MatProgressSpinnerModule,
    DxNumberBoxModule,
    MatIconModule,
    DxDateBoxModule,
    DxHtmlEditorModule,
    MatRadioModule
  ],
  providers: [
    { provide: APP_CONFIG_TOKEN, useValue: applicationConfigs },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppInsights,
      deps: [ApplicationInsightsService],
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    // { provide: HTTP_INTERCEPTORS, useClass: ApiUrlInterceptor, multi: true },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: BROWSER_STORAGE, useValue: sessionStorage },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(httpClient: HttpClient) {
    devextremeAjax.inject({ sendRequest: sendRequestFactory(httpClient) });
  }
}
